import { axios, getResponse, settings } from '../../helpers/main';

const { backend } = settings;
const objectService = getResponse({ type: 0 }); // eslint-disable-line

/**
 * Service to manage communication with the Accounts API for Password Recovery.
 * Follows the Single Responsibility Principle (SRP) by separating network logic from the UI.
 */
export const PasswordRecoverService = {
    /**
     * Submits a request to recover a forgotten password.
     * @param {Object} params - Parameters.
     * @param {string} params.email - User email to recover the password.
     * @param {string} params.reCaptchaToken - Google ReCAPTCHA verification token.
     * @param {number} params.languageId - Current language ID of the application.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    recoverPasswordAsync: async ({
        email,
        reCaptchaToken,
        languageId,
        subMethod = ''
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.accounts}${backend?.urls?.recoverPassword}/`,
            headers: {},
            data: {
                email: email,
                reCaptchaToken: reCaptchaToken,
                languageId: languageId,
                method: backend?.methods?.users.passwordRecoverGet,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Checks if a password recovery token/ID is valid.
     * @param {Object} params - Parameters.
     * @param {number} params.id - User ID or recovery token.
     * @param {number} params.languageId - Current language ID of the application.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    passwordRecoverCheckAsync: async ({
        id,
        languageId,
        subMethod = ''
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.accounts}${backend?.urls?.recoverPassword}/`,
            headers: {},
            data: {
                id: id,
                languageId: languageId,
                method: backend?.methods?.users.passwordRecoverCheck,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Submits a request to change the user's password using the recovery token/ID.
     * @param {Object} params - Parameters.
     * @param {number} params.id - User ID or recovery token.
     * @param {string} params.password - New password.
     * @param {string} params.passwordConfirm - Password confirmation.
     * @param {string} params.reCaptchaToken - Google ReCAPTCHA verification token.
     * @param {number} params.languageId - Current language ID of the application.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    changePasswordAsync: async ({
        id,
        password,
        passwordConfirm,
        reCaptchaToken,
        languageId,
        subMethod = ''
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.accounts}${backend?.urls?.recoverPassword}/`,
            headers: {},
            data: {
                id: id,
                password: password,
                passwordConfirm: passwordConfirm,
                reCaptchaToken: reCaptchaToken,
                languageId: languageId,
                method: backend?.methods?.users.passwordRecoverChangePassword,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },
};